import { useNavigation } from "@react-navigation/native";
import { Text } from "@ui-kitten/components";
import Button from "components/Button";
import { openBrowserAsync } from "expo-web-browser";
import { TicketDetails } from "features/types";
import { useAccount } from "hooks/useAccount";
import i18n from "plugins/localization";
import { useFetchTenantConfigurationQuery } from "queries/configuration.queries";
import React from "react";
import { Pressable, StyleSheet, View } from "react-native";
import { barcodeHoursBefore } from "utils/ticketUtils";

interface EventDetailFooterProps {
  tickets: TicketDetails[];
  groupCode: string;
}

const OpenLink = ({ text, onPress }: { text: string; onPress: () => void }) => (
  <Pressable onPress={onPress}>
    <Text status="info" category="c1">
      {text}
    </Text>
  </Pressable>
);

const EventDetailFooter: React.FC<EventDetailFooterProps> = ({
  tickets,
  groupCode,
}) => {
  const { barcodeMinutesBefore, barcodeReady, barcodeProvider } = tickets[0];
  const { data: tenantConfig } = useFetchTenantConfigurationQuery();
  const navigation: any = useNavigation();

  // if the value is set to FANRALLY, then we are able to display barcodes within our UI.
  const barcodesDeliveredInternally = barcodeProvider === "FANRALLY";

  const { account } = useAccount();
  const openBallParkApp = () => {
    openBrowserAsync("https://bp.mlb.com/wallet");
  };

  const hasTransferableTicket = tickets.some(
    (ticket) => ticket.transferability !== "NONE"
  );

  const BarcodesDeliveredToBallpark = () => (
    <>
      <Text>
        Your entry barcodes will be delivered to your mobile device in the{" "}
      </Text>
      <OpenLink text="Ballpark App" onPress={openBallParkApp} />,{" "}
      <Text>inside the following MLB Ballpark account:</Text>
      <Text category="s2">{account?.extUserDetails?.tdcUsername!}</Text>
    </>
  );

  const handleShowBarcodes = () => {
    navigation.navigate("Barcode", { groupCode });
  };

  const BarcodesDeliveredToAXS = () => (
    <>
      <Text>
        Your tickets will be available within the{" "}
        {tenantConfig?.mobileAppName || tickets[0].event.tenantAlias} app in the
        following AXS account:
      </Text>
      <Text category="s2">{account?.extUserDetails?.axsUsername!}</Text>
      <br />
      <Text>{i18n.t("components.ShareDialog.bookingIsFinal")}</Text>
    </>
  );

  const BarcodesDeliveredToTFL = () => (
    <Text>
      Your tickets will be delivered before your event. You'll receive an email
      from the venue's ticketing platform.
    </Text>
  );

  const BarcodesDeliveredExternally = () => {
    return (
      <>
        {barcodeProvider === "TDC" && <BarcodesDeliveredToBallpark />}
        {barcodeProvider === "AXS" && <BarcodesDeliveredToAXS />}
        {barcodeProvider === "TFL" && <BarcodesDeliveredToTFL />}
      </>
    );
  };

  return (
    <>
      {barcodesDeliveredInternally ? (
        <View>
          <Button
            style={{ marginVertical: 15, width: "80%", alignSelf: "center" }}
            onPress={handleShowBarcodes}
            disabled={!barcodeReady}
            status={barcodeReady ? "primary" : "basic"}
          >
            {i18n.t("screens.EventDetail.showBarcodes")}
          </Button>
        </View>
      ) : (
        <Text category="c1" style={{ textAlign: "center" }}>
          <BarcodesDeliveredExternally />
        </Text>
      )}

      {/* drop the "Barcodes will be available X hours before the game" text, as this means nothing in an AXS context. */}
      {barcodeProvider !== "AXS" && barcodeProvider !== "TFL" && (
        <Text style={[style.barcodeText, { fontWeight: "700" }]}>
          {i18n.t("screens.EventDetail.barcodeMinutesBefore", {
            barcodeMinutesBefore: barcodeHoursBefore(barcodeMinutesBefore),
          })}
        </Text>
      )}

      {!hasTransferableTicket && !tenantConfig?.hideTransferabilityText && (
        <Text category="c1" style={{ textAlign: "center", marginTop: 10 }}>
          {i18n.t("screens.EventDetail.reservationsNonTransferable")}
        </Text>
      )}
    </>
  );
};

const style = StyleSheet.create({
  iconContainer: {
    borderWidth: 1,
    borderRadius: 36,
    padding: 6,
    borderColor: "rgb(187,187,187)",
  },
  barcodeText: {
    paddingHorizontal: 15,
    textAlign: "center",
    marginBottom: 15,
  },
});
export default EventDetailFooter;
